import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { githubWorkflowsApiRef } from '@veecode-platform/github-workflows-common';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import CloudIcon from '@material-ui/icons/Cloud';
import { formatDistanceToNow } from 'date-fns';

const NEXGEN_REPO = 'exo-imaging/cloud-build-nexgen-infra';
const NEXGEN_HOSTNAME = 'github.com';

interface RunItem {
  id: number;
  status: string;
  conclusion: string | null;
  created_at: string;
  updated_at: string;
  html_url: string;
  run_number: number;
  actor: { login: string; avatar_url: string } | null;
  triggeredByUserRef: string | null;
  inputs: Record<string, string> | null;
}

interface RunHistoryData {
  workflow: { id: number; name: string } | null;
  runs: RunItem[];
}

interface ServiceTag {
  repo: string;
  displayName: string;
  tag: string;
  date: string | null;
}

interface DeployedService {
  name: string;
  version: string | null;
  commit: string | null;
  branch: string | null;
  releaseName: string | null;
  repo: string | null;
  deployedTag: string | null;
  releaseNotesUrl: string | null;
}

interface DeployedEnv {
  env: string;
  services: DeployedService[];
}

const ENV_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  dev: { bg: 'rgba(14,165,233,0.08)', text: '#0ea5e9', border: 'rgba(14,165,233,0.3)' },
  qa: { bg: 'rgba(249,115,22,0.08)', text: '#f97316', border: 'rgba(249,115,22,0.3)' },
  uat: { bg: 'rgba(16,185,129,0.08)', text: '#10b981', border: 'rgba(16,185,129,0.3)' },
  sandbox: { bg: 'rgba(99,102,241,0.08)', text: '#6366f1', border: 'rgba(99,102,241,0.3)' },
};

const useStyles = makeStyles(theme => ({
  section: { marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: '2px solid rgba(255,255,255,0.08)',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  accent: {
    width: 4, height: 20, borderRadius: 2,
    backgroundColor: '#7df3e1',
    display: 'inline-block', marginRight: theme.spacing(1),
  },
  // ── Service Tags ──
  tagCard: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: theme.shape.borderRadius,
    height: '100%',
    '&:hover': { borderColor: 'rgba(125,243,225,0.3)' },
  },
  tagName: {
    fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 700,
    color: '#7df3e1', marginBottom: theme.spacing(0.5),
  },
  tagRepo: { fontSize: '0.72rem', color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5) },
  tagDate: { fontSize: '0.72rem', color: theme.palette.text.secondary },
  // ── Deployed Versions ──
  envCard: {
    borderRadius: theme.shape.borderRadius,
    border: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.02)',
    height: '100%',
  },
  envLabel: {
    fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: theme.spacing(1.5),
  },
  serviceBlock: {
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    '&:last-child': { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 },
  },
  serviceName: { fontWeight: 600, fontSize: '0.82rem', marginBottom: 4 },
  serviceVersion: {
    fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: '#7df3e1',
  },
  serviceMeta: { fontSize: '0.72rem', color: theme.palette.text.secondary, marginTop: 2 },
  serviceCommit: { fontFamily: 'monospace', fontSize: '0.72rem', color: '#9ca3af' },
  // ── Tables ──
  table: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  tableHead: { backgroundColor: 'rgba(255,255,255,0.04)' },
  tableHeadCell: {
    fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: theme.palette.text.secondary,
    borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '10px 16px',
  },
  tableCell: {
    fontSize: '0.83rem', borderBottom: '1px solid rgba(255,255,255,0.05)',
    padding: '10px 16px', verticalAlign: 'middle',
  },
  emptyRow: { textAlign: 'center', color: theme.palette.text.secondary, padding: theme.spacing(3) },
  // ── Status chips ──
  chipSuccess: { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipFailure: { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipInProgress: { backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipCancelled: { backgroundColor: 'rgba(156,163,175,0.15)', color: '#9ca3af', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  // ── Env chips ──
  chipDev: { backgroundColor: 'rgba(14,165,233,0.15)', color: '#0ea5e9', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipQa: { backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipUat: { backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipSandbox: { backgroundColor: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  chipDefault: { backgroundColor: 'rgba(107,114,128,0.15)', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', height: 22 },
  // ── Tag chips in deploy table ──
  tagChip: {
    fontFamily: 'monospace', fontSize: '0.68rem', height: 20,
    backgroundColor: 'rgba(125,243,225,0.1)', color: '#7df3e1',
    marginRight: 4, marginBottom: 2,
  },
  // ── Env filter chips ──
  filterRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1.5) },
  filterChip: { cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem' },
  filterChipActive: { fontWeight: 700 },
  // ── Misc ──
  actorText: { fontSize: '0.82rem', color: theme.palette.text.secondary },
  timeText: { fontSize: '0.82rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap' },
  linkBtn: { padding: 4, color: theme.palette.text.secondary, '&:hover': { color: '#7df3e1' } },
  noData: { color: theme.palette.text.secondary, fontStyle: 'italic', fontSize: '0.85rem' },
  unavailable: { color: '#6b7280', fontSize: '0.82rem', fontStyle: 'italic' },
}));

// ── Helper components ──

function StatusChip({ status, conclusion }: { status: string; conclusion: string | null }) {
  const classes = useStyles();
  if (status === 'in_progress' || status === 'queued') return <Chip label="In Progress" className={classes.chipInProgress} size="small" />;
  switch (conclusion) {
    case 'success': return <Chip label="Success" className={classes.chipSuccess} size="small" />;
    case 'failure': return <Chip label="Failed" className={classes.chipFailure} size="small" />;
    case 'cancelled': return <Chip label="Cancelled" className={classes.chipCancelled} size="small" />;
    default: return <Chip label={conclusion ?? status} className={classes.chipDefault} size="small" />;
  }
}

function EnvChip({ env }: { env: string }) {
  const classes = useStyles();
  const lower = (env ?? '').toLowerCase();
  if (lower === 'dev' || lower === 'development') return <Chip label="dev" className={classes.chipDev} size="small" />;
  if (lower === 'qa') return <Chip label="qa" className={classes.chipQa} size="small" />;
  if (lower === 'uat') return <Chip label="uat" className={classes.chipUat} size="small" />;
  if (lower === 'sandbox') return <Chip label="sandbox" className={classes.chipSandbox} size="small" />;
  if (!lower) return <Chip label="—" className={classes.chipDefault} size="small" />;
  return <Chip label={lower} className={classes.chipDefault} size="small" />;
}

function relativeTime(dateStr: string | null) {
  if (!dateStr) return '—';
  try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); } catch { return dateStr; }
}

// GitHub always attributes dispatched runs to the owner of the shared PAT
// used to call the API — not the Backstage user who clicked deploy/build.
const GITHUB_PAT_LOGINS = ['sriram-mudumbi'];

function formatActorLogin(login: string | undefined | null) {
  if (!login) return '—';
  return GITHUB_PAT_LOGINS.includes(login) ? 'GitHub PAT' : login;
}

// Our own audit trail (see backend dispatchAudit.ts) records the real
// Backstage user as a userEntityRef, e.g. "user:default/ronit-gandhi".
function formatUserId(userEntityRef: string | null | undefined) {
  if (!userEntityRef) return '—';
  const localPart = userEntityRef.split('/').pop();
  return localPart || userEntityRef;
}

function DeployTagChips({ inputs, classes }: { inputs: Record<string, string> | null; classes: ReturnType<typeof useStyles> }) {
  if (!inputs) return <span className={classes.noData}>—</span>;
  // Match any input key that looks like an image tag or version tag
  const tagKeys = Object.keys(inputs).filter(k =>
    (k.endsWith('_image_tag') || k.endsWith('_tag') || k.endsWith('_version')) &&
    !k.startsWith('target_') &&
    inputs[k],
  );
  if (tagKeys.length === 0) return <span className={classes.noData}>—</span>;
  return (
    <Box display="flex" flexWrap="wrap">
      {tagKeys.map(k => (
        <Tooltip key={k} title={k}>
          <Chip label={inputs[k]} className={classes.tagChip} size="small" />
        </Tooltip>
      ))}
    </Box>
  );
}

// ── Main component ──

export const ReleaseHistoryPage = () => {
  const classes = useStyles();
  const workflowsApi = useApi(githubWorkflowsApiRef);

  const [deployData, setDeployData] = useState<RunHistoryData | null>(null);
  const [buildData, setBuildData] = useState<RunHistoryData | null>(null);
  const [serviceTags, setServiceTags] = useState<ServiceTag[]>([]);
  const [deployedVersions, setDeployedVersions] = useState<DeployedEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deployEnvFilter, setDeployEnvFilter] = useState<string>('all');

  const fetchAll = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      (workflowsApi as any).listRunHistory(NEXGEN_HOSTNAME, NEXGEN_REPO, 'deploy all', 20),
      (workflowsApi as any).listRunHistory(NEXGEN_HOSTNAME, NEXGEN_REPO, 'build all', 20),
      (workflowsApi as any).getServiceTags(NEXGEN_HOSTNAME, NEXGEN_REPO),
      (workflowsApi as any).getDeployedVersions(),
    ])
      .then(([deploy, build, tags, versions]: any[]) => {
        setDeployData(deploy);
        setBuildData(build);
        setServiceTags(Array.isArray(tags) ? tags : []);
        setDeployedVersions(Array.isArray(versions) ? versions : []);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err?.message ?? 'Failed to load release history');
        setLoading(false);
      });
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Box p={3}><Typography color="error">Failed to load release history: {error}</Typography></Box>;
  }

  // Filter deploy runs by selected env
  const filteredDeployRuns = deployData?.runs.filter(run => {
    if (deployEnvFilter === 'all') return true;
    const env = (run.inputs?.manual_deployment_env ?? '').toLowerCase();
    return env === deployEnvFilter || (deployEnvFilter === 'dev' && env === 'development');
  }) ?? [];

  // Build env filter options from actual run data
  const runEnvs = [...new Set(
    (deployData?.runs ?? [])
      .map(r => (r.inputs?.manual_deployment_env ?? '').toLowerCase())
      .filter(Boolean)
  )].sort();

  return (
    <Box>

      {/* ── Latest Service Tags (GitHub) ── */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span className={classes.accent} />
            <LocalOfferIcon style={{ fontSize: 16, marginRight: 6, color: '#7df3e1' }} />
            Latest Released Tags (GitHub)
          </Typography>
          <IconButton size="small" className={classes.linkBtn} onClick={fetchAll}><RefreshIcon fontSize="small" /></IconButton>
        </Box>
        <Grid container spacing={2}>
          {serviceTags.length === 0 && (
            <Grid item xs={12}><Typography className={classes.noData}>No tag data available.</Typography></Grid>
          )}
          {serviceTags.map(st => (
            <Grid item xs={12} sm={4} key={st.repo}>
              <Card className={classes.tagCard} elevation={0}>
                <CardContent>
                  <Typography className={classes.tagRepo}>{st.repo}</Typography>
                  <Typography className={classes.tagName}>{st.tag || '—'}</Typography>
                  <Typography className={classes.tagDate}>{st.date ? relativeTime(st.date) : 'date unknown'}</Typography>
                  <Typography variant="subtitle2" style={{ marginTop: 4 }}>{st.displayName}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Currently Deployed Versions ── */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span className={classes.accent} style={{ backgroundColor: '#22c55e' }} />
            <CloudIcon style={{ fontSize: 16, marginRight: 6, color: '#22c55e' }} />
            Currently Deployed Versions
          </Typography>
          <IconButton size="small" className={classes.linkBtn} onClick={fetchAll}><RefreshIcon fontSize="small" /></IconButton>
        </Box>
        <Grid container spacing={2}>
          {deployedVersions.length === 0 && (
            <Grid item xs={12}><Typography className={classes.noData}>Could not fetch deployed versions.</Typography></Grid>
          )}
          {deployedVersions.map(envData => {
            const colors = ENV_COLORS[envData.env] ?? ENV_COLORS.dev;
            return (
              <Grid item xs={12} sm={6} key={envData.env}>
                <Card
                  className={classes.envCard}
                  elevation={0}
                  style={{ borderColor: colors.border }}
                >
                  <CardContent>
                    <Typography
                      className={classes.envLabel}
                      style={{ color: colors.text }}
                    >
                      {envData.env.toUpperCase()}
                    </Typography>
                    {envData.services.length === 0 && (
                      <Typography className={classes.unavailable}>Unavailable</Typography>
                    )}
                    {envData.services.map((svc, i) => (
                      <Box key={i} className={classes.serviceBlock}>
                        <Typography className={classes.serviceName}>{svc.name}</Typography>
                        {/* Deployed tag from RELEASE_NOTES URL (primary identifier) */}
                        {svc.deployedTag ? (
                          <Tooltip title={svc.releaseNotesUrl ?? svc.deployedTag}>
                            <Typography className={classes.serviceVersion} style={{ cursor: svc.releaseNotesUrl ? 'pointer' : 'default' }}
                              onClick={() => svc.releaseNotesUrl && window.open(svc.releaseNotesUrl, '_blank')}
                            >
                              {svc.deployedTag}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography className={classes.serviceVersion}>
                            {svc.version ?? <span className={classes.unavailable}>—</span>}
                          </Typography>
                        )}
                        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 4 }}>
                          {/* Show version separately if we already showed tag above */}
                          {svc.deployedTag && svc.version && svc.version !== svc.deployedTag && (
                            <Typography className={classes.serviceMeta}>
                              build: {svc.version}
                            </Typography>
                          )}
                          {svc.commit && (
                            <Typography className={classes.serviceCommit}>
                              commit: {svc.commit}
                            </Typography>
                          )}
                          {svc.branch && (
                            <Typography className={classes.serviceMeta}>
                              branch: {svc.branch}
                            </Typography>
                          )}
                          {svc.releaseName && (
                            <Typography className={classes.serviceMeta}>
                              release: {svc.releaseName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* ── Deploy History ── */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span className={classes.accent} style={{ backgroundColor: '#6366f1' }} />
            Deploy History
            {deployData?.workflow && (
              <Typography component="span" style={{ fontSize: '0.72rem', color: '#6b7280', marginLeft: 8 }}>
                {deployData.workflow.name} · last {deployData.runs.length}
              </Typography>
            )}
          </Typography>
          <IconButton size="small" className={classes.linkBtn} onClick={fetchAll}><RefreshIcon fontSize="small" /></IconButton>
        </Box>

        {/* Env filter chips */}
        <Box className={classes.filterRow}>
          <Typography style={{ fontSize: '0.78rem', color: '#6b7280', marginRight: 4 }}>Filter:</Typography>
          {['all', ...runEnvs].map(envOpt => {
            const isActive = deployEnvFilter === envOpt;
            const colors = envOpt === 'all' ? null : ENV_COLORS[envOpt];
            return (
              <Chip
                key={envOpt}
                label={envOpt === 'all' ? 'All' : envOpt.toUpperCase()}
                size="small"
                onClick={() => setDeployEnvFilter(envOpt)}
                className={`${classes.filterChip} ${isActive ? classes.filterChipActive : ''}`}
                style={{
                  backgroundColor: isActive
                    ? colors ? colors.bg : 'rgba(125,243,225,0.12)'
                    : 'rgba(255,255,255,0.05)',
                  color: isActive
                    ? colors ? colors.text : '#7df3e1'
                    : '#9ca3af',
                  border: isActive
                    ? `1px solid ${colors ? colors.border : 'rgba(125,243,225,0.4)'}`
                    : '1px solid rgba(255,255,255,0.08)',
                }}
              />
            );
          })}
          {deployEnvFilter !== 'all' && (
            <Typography style={{ fontSize: '0.72rem', color: '#6b7280' }}>
              {filteredDeployRuns.length} run{filteredDeployRuns.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Box className={classes.table}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                <TableCell className={classes.tableHeadCell}>Environment</TableCell>
                <TableCell className={classes.tableHeadCell}>Tags Deployed</TableCell>
                <TableCell className={classes.tableHeadCell}>Triggered By</TableCell>
                <TableCell className={classes.tableHeadCell}>User ID</TableCell>
                <TableCell className={classes.tableHeadCell}>Time</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDeployRuns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyRow}>
                    {deployEnvFilter === 'all' ? 'No deploy runs found.' : `No ${deployEnvFilter} deploy runs found.`}
                  </TableCell>
                </TableRow>
              )}
              {filteredDeployRuns.map(run => (
                <TableRow key={run.id} hover>
                  <TableCell className={classes.tableCell}>
                    <StatusChip status={run.status} conclusion={run.conclusion} />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <EnvChip env={run.inputs?.manual_deployment_env ?? ''} />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <DeployTagChips inputs={run.inputs} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <span className={classes.actorText}>{formatActorLogin(run.actor?.login)}</span>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <span className={classes.actorText}>{formatUserId(run.triggeredByUserRef)}</span>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title={new Date(run.created_at).toLocaleString()}>
                      <span className={classes.timeText}>{relativeTime(run.created_at)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    <Tooltip title="Open in GitHub">
                      <IconButton size="small" className={classes.linkBtn} onClick={() => window.open(run.html_url, '_blank')}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* ── Build History ── */}
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span className={classes.accent} style={{ backgroundColor: '#f59e0b' }} />
            Build History
            {buildData?.workflow && (
              <Typography component="span" style={{ fontSize: '0.72rem', color: '#6b7280', marginLeft: 8 }}>
                {buildData.workflow.name} · last {buildData.runs.length}
              </Typography>
            )}
          </Typography>
          <IconButton size="small" className={classes.linkBtn} onClick={fetchAll}><RefreshIcon fontSize="small" /></IconButton>
        </Box>
        <Box className={classes.table}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                <TableCell className={classes.tableHeadCell}>Triggered By</TableCell>
                <TableCell className={classes.tableHeadCell}>User ID</TableCell>
                <TableCell className={classes.tableHeadCell}>Duration</TableCell>
                <TableCell className={classes.tableHeadCell}>Time</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!buildData?.runs || buildData.runs.length === 0) && (
                <TableRow><TableCell colSpan={6} className={classes.emptyRow}>No build runs found.</TableCell></TableRow>
              )}
              {buildData?.runs.map(run => {
                const durationMs = run.updated_at && run.created_at
                  ? new Date(run.updated_at).getTime() - new Date(run.created_at).getTime()
                  : null;
                const durationStr = durationMs !== null && durationMs > 0
                  ? durationMs >= 60000
                    ? `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`
                    : `${Math.floor(durationMs / 1000)}s`
                  : '—';
                return (
                  <TableRow key={run.id} hover>
                    <TableCell className={classes.tableCell}>
                      <StatusChip status={run.status} conclusion={run.conclusion} />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.actorText}>{formatActorLogin(run.actor?.login)}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.actorText}>{formatUserId(run.triggeredByUserRef)}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.timeText}>{durationStr}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Tooltip title={new Date(run.created_at).toLocaleString()}>
                        <span className={classes.timeText}>{relativeTime(run.created_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      <Tooltip title="Open in GitHub">
                        <IconButton size="small" className={classes.linkBtn} onClick={() => window.open(run.html_url, '_blank')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

    </Box>
  );
};
